import React, {useState,useEffect} from "react";
import axios from "../api/axios";
import { useNavigate, useParams, Link, useLocation } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { convertDateFull, formatMoney } from '../utils/helpers';
import dompurify from 'dompurify';
import styles from './EventDetails.module.css';
//import ProjectRegistration from "./EventRegistration/ProjectRegistration";
import DRRegistration from "./EventRegistration/DRRegistration";
//import CampRegistration from "./EventRegistration/CampRegistration";
import EventRegistration from "./EventRegistration/EventRegistration"

function getInitials(fullName) {
  if (!fullName || typeof fullName !== 'string') return '?';
  const parts = fullName.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function getRequiredShotLabelList(BP_RequiredShots, immunizationsList) {
  if (!BP_RequiredShots || !Array.isArray(immunizationsList)) return [];
  return BP_RequiredShots.split(';')
    .map((id) => id.trim())
    .filter(Boolean)
    .map((id) => immunizationsList.find((shot) => String(shot.id) === id)?.label)
    .filter(Boolean);
}

/** Normalize common age labels for display (e.g. API "16+ Years", "18+" → mock copy). */
function formatAgeGradeDisplay(raw) {
  const v = String(raw ?? '').trim();
  if (!v) return v;
  if (/^16\s*\+\s*years?$/i.test(v)) return '16 Years and Up';
  if (/^16\s*\+$/i.test(v)) return '16 Years and Up';
  if (/^18\s*\+\s*years?$/i.test(v)) return '18 Years and Up';
  if (/^18\s*\+$/i.test(v)) return '18 Years and Up';
  return v;
}

const EventDetails = (props) => {
  const location = useLocation();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const [bomEvent, setbomEvent] = useState({});
  const [registrations, setRegistrations] = useState([]);
  const {eventid} = useParams();
  const [contentLoaded, setContentLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);
  const [coordinatorObj, setCoordinatorObj] = useState({});
  const [administratorObj, setAdministratorObj]= useState({});
  const [showReservedInfo, setShowReservedInfo] = useState(false);
  const [teamLeaderName, setTeamLeaderName] = useState('');
  const [immunizationsList, setImmunizationsList] = useState([]);
  
  const navigate = useNavigate();
 
  const getEventsData = (id) => {
    let endpoints = [
      `/api/events/${id}`,
      `/api/registrations/events/${id}`,
      `/api/immunizations`
    ];
    Promise.all(endpoints.map((endpoint) => axios.get(endpoint)))
      .then(([{ data: eventInfo }, { data: registrations }, {data: immunizations}]) => {
        setbomEvent(eventInfo);
        setRegistrations(registrations);
        setImmunizationsList(immunizations)
        setIsLoading(false);
        setContentLoaded(true);
        //console.log(eventInfo);
      })
      .catch((err) => {
        console.error('Failed to load event data:', err);
        setError(true);
        setIsLoading(false);
      });
  }

  const getSupportingData = async (coordid, adminid) => {
    try {
      const endpoints = [];
      if (coordid && coordid !== 0) {
        endpoints.push(axios.get(`/api/users/${coordid}`));
      } else {
        endpoints.push(Promise.resolve({ data: {} }));
      }
      
      if (adminid && adminid !== 0) {
        endpoints.push(axios.get(`/api/users/${adminid}`));
      } else {
        endpoints.push(Promise.resolve({ data: {} }));
      }
      
      const [{data: coord}, {data: admin}] = await Promise.all(endpoints);
      setCoordinatorObj(coord);
      setAdministratorObj(admin);
      //console.log('Coordinator:', coord, 'Admin:', admin);
    } catch (error) {
      console.error('Error fetching coordinator/administrator data:', error);
    }
  }

  const extractTeamLeaderName = (reserveInstructions) => {
    if (!reserveInstructions) return '';
    const match = reserveInstructions.match(/reserved by ([^<]+?) as team leader/i);
    return match ? match[1].trim() : '';
  }

  const toggleReservedInfo = () => {
    setShowReservedInfo(!showReservedInfo);
  }

  // ── Price range helper ──────────────────────────────────────────────
  const renderPriceRange = () => {
    if (!registrations || registrations.length === 0) {
      return <p>No cost information available</p>;
    }

    const prices = registrations.reduce((acc, reg) => {
      if (reg.RegistrationRequiredCost && reg.RegistrationRequiredCostName) {
        const cost = parseFloat(String(reg.RegistrationRequiredCost).replace(/[^0-9.]/g, ''));
        if (!isNaN(cost)) acc.push(cost);
      } else if (reg.RegistrationCost && reg.RegistrationCost > 0) {
        acc.push(reg.RegistrationCost);
      }
      return acc;
    }, []);

    if (prices.length === 0) {
      return <p>No cost information available</p>;
    }

    const lowest = Math.min(...prices);
    const highest = Math.max(...prices);

    return (
      <p className={styles.costItem}>
        {lowest === highest
          ? formatMoney(lowest)
          : `${formatMoney(lowest)} - ${formatMoney(highest)}`
        }
      </p>
    );
  }
  // ───────────────────────────────────────────────────────────────────

  useEffect(() => {
    getEventsData(eventid);
  }, [eventid]);

  useEffect(() => {
    //if (!bomEvent?.BP_NCBMManaged) return;
    const coord = bomEvent.BP_Coordinator;
    const admin = bomEvent.BP_Administrator;
    if ((!coord || coord === 0) && (!admin || admin === 0)) return;
    getSupportingData(coord || 0, admin || 0);
  }, [bomEvent?.BP_Coordinator, bomEvent?.BP_Administrator]);

  useEffect(() => {
    if (bomEvent?.BP_ReserveInstructions) {
      const name = extractTeamLeaderName(bomEvent.BP_ReserveInstructions);
      setTeamLeaderName(name);
    }
  }, [bomEvent?.BP_ReserveInstructions]);

  const bgImageStyle = {
    background: `url("/Images/eventBanner.jpg") no-repeat center center`,
    backgroundSize: 'cover'
  }

  const returnRegistrations = () => {
    if (!contentLoaded || !bomEvent || Object.keys(bomEvent).length === 0) {
      return null;
    }

    if (!bomEvent?.BP_Type) {
      return <p>No Type Selected on event</p>;
    }

    switch (bomEvent?.BP_Type) {
      case "Project":
        return <EventRegistration Registrations={registrations} Event={bomEvent} />;
      case "Disaster Relief":
        return <DRRegistration Registrations={registrations} Event={bomEvent} />;
      case "Camp":
        return <EventRegistration Registrations={registrations} Event={bomEvent} />;
      default:
        return <EventRegistration Registrations={registrations} Event={bomEvent} />;
    }
  }

  const immunizationLabels = getRequiredShotLabelList(bomEvent?.BP_RequiredShots, immunizationsList);

  const contactEntries = [];
    if (coordinatorObj?.FirstName) {
      contactEntries.push({
        name: coordinatorObj.FirstName + " " + coordinatorObj.LastName,
        email: coordinatorObj.Email,
        phone: coordinatorObj.userSettings?.BP_G_Mob_Phone,
        showEmail: bomEvent.BP_ShowCoordinatorEmail,
        showPhone: bomEvent.BP_ShowCoordinatorPhone,
      });
    }
    if (administratorObj?.FirstName) {
      contactEntries.push({
        name: administratorObj.FirstName + " " + administratorObj.LastName,
        email: administratorObj.Email,
        phone: administratorObj.userSettings?.BP_G_Mob_Phone,
        showEmail: bomEvent.BP_ShowAdministratorEmail,
        showPhone: bomEvent.BP_ShowAdministratorPhone,
      });
    }

  const restrictionRows = [];
  if (bomEvent?.BP_AgeGradeRestrictions?.trim()) {
    restrictionRows.push({ label: 'Grade', value: bomEvent.BP_AgeGradeRestrictions.trim() });
  }
  if (bomEvent?.BP_AgeGrade?.trim() && bomEvent.BP_AgeGrade.trim() !== '(none)') {
    restrictionRows.push({ label: 'Age', value: formatAgeGradeDisplay(bomEvent.BP_AgeGrade.trim()) });
  }
  if (bomEvent?.BP_Gender?.trim() && bomEvent.BP_Gender.trim() !== '(none)') {
    restrictionRows.push({ label: 'Gender', value: bomEvent.BP_Gender.trim() });
  }
  if (bomEvent?.BP_OverNight?.trim() && bomEvent.BP_OverNight.trim() !== '(none)') {
    restrictionRows.push({ label: 'Overnight', value: bomEvent.BP_OverNight.trim() });
  }
  const hasRestrictions = restrictionRows.length > 0;

  return (
    <div id="">
      <div id="banner" className="full-row" style={bgImageStyle}>
        <div className="overlay"></div>
        <div className="banner-content">
          <div className="container-fluid">
            <div className="row">
              <div className="col-md-12 text-white">
                <h1 className="text-uppercase">{bomEvent.BP_Title}</h1>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Blue Banner Bar */}
      <div className="full-row light-blue">
        <div className="container-fluid">
          <div className="row text-white pt-3 mb-0 event-highlights">
            <div className="col-md-4 col pt-3 pb-2 right-divide">
              <h6>Location</h6>
              <p>{bomEvent.BP_Address}</p>
            </div>
            <div className="col-md-4 col pt-3 pb-2 right-divide">
              <h6>Dates</h6>
              {bomEvent.BP_ShowDateOnly ?
                <p>{`${convertDateFull(bomEvent.BP_StartDate, false)}${bomEvent.BP_ShowEndTime ? ` - ${convertDateFull(bomEvent.BP_EndDate, false)}` : ''}`}</p>
                :
                <p>{`${convertDateFull(bomEvent.BP_StartDate, true)}${bomEvent.BP_ShowEndTime ? ` - ${convertDateFull(bomEvent.BP_EndDate, true)}` : ''}`}</p>
              }
            </div>
            <div className="col-md-4 col pt-3 pb-2 right-divide">
              <h6>Costs</h6>
              {renderPriceRange()}  {/* ← replaced the map block */}
            </div>
          </div>
        </div>
      </div>
      {/* End Blue Banner Bar */}

      {/* White Banner Bar */}
      <div className="full-row white">
        <div className="container-fluid">
          <div className="row">
            <div className="col-md-12">
              {bomEvent.BP_Reserved ?
                <div>
                  <div className={styles.reservedContainer}>
                    <span className="reserved-label">Reserved</span>
                    <button
                      onClick={toggleReservedInfo}
                      className={styles.seeMoreButton}
                    >
                      See More <span className={`${styles.arrowIcon} ${showReservedInfo ? styles.rotated : ''}`}><i className="fas fa-chevron-down"></i></span>
                    </button>
                  </div>
                  <div className={`${styles.reservedInfoWrapper} ${showReservedInfo ? styles.expanded : styles.collapsed}`}>
                    <div className={styles.reservedInfoContent}>
                      <div className={styles.reservedInfoText}>
                        {bomEvent.BP_ReserveInstructions && (
                          <>
                          <div dangerouslySetInnerHTML={{ __html: dompurify.sanitize(bomEvent?.BP_ReserveInstructions) }} />
                          </>
                        )}
                        {teamLeaderName && (
                          <>
                            <strong>{teamLeaderName}</strong> is the team leader. Only team members may sign up for this project,
                            using the designated "team name". If you are a team member but do not know the team name, see your team leader.
                          </>
                        )}
                        {!bomEvent.BP_NCBMManaged ? bomEvent.BP_ContactName : ""}
                        {!bomEvent.BP_NCBMManaged ? bomEvent.BP_ContactEmail : ""}
                        {!bomEvent.BP_NCBMManaged ? bomEvent.BP_ContactPhone : ""}
                      </div>
                    </div>
                  </div>
                </div>
                :
                <div></div>
              }
            </div>
          </div>
        </div>
      </div>
      {/* End White Banner Bar */}

      {/* Event details, sidebar (contact / restrictions / immunizations), registration */}
      <div className="full-row white">
        <div className="container-fluid">
          {isLoading ? (
            <div className="text-center py-5">
              <p>Loading event details...</p>
            </div>
          ) : error ? (
            <div className="text-center py-5">
              <p>Error loading event. Please try again.</p>
            </div>
          ) : (
            <div className={styles.eventContentGrid}>
              <div className={styles.eventDescription}>
                <div dangerouslySetInnerHTML={{ __html: dompurify.sanitize(bomEvent?.BP_Description) }} />
              </div>

              <aside className={`${styles.eventSidebar} ${styles.sidebarCard}`} aria-label="Event contact and requirements">
                <section>
                  <h2 className={styles.sidebarSectionTitle}>Contact</h2>
                  {contactEntries.length === 0 ? (
                    <p className="text-muted mb-0">No contact information available.</p>
                  ) : (
                    contactEntries.map((c, i) => (
                      <div key={`${c.name}-${i}`} className={styles.contactBlock}>
                        <div className={styles.contactAvatar} aria-hidden="true">
                          {getInitials(c.name)}
                        </div>
                        <div className={styles.contactMeta}>
                          <div className={styles.contactName}>{c.name}</div>
                          {c.showEmail && c.email && (
                            <a className={styles.contactEmail} href={`mailto:${c.email}`}>
                              {c.email}
                            </a>
                          )}
                          {c.showPhone && c.phone && (() => {
                            const digits = String(c.phone).replace(/\D/g, '');
                            if (!digits) {
                              return <span className={styles.contactPhone}>{c.phone}</span>;
                            }
                            return (
                              <a className={styles.contactPhone} href={`tel:${digits}`}>
                                {c.phone}
                              </a>
                            );
                          })()}
                        </div>
                      </div>
                    ))
                  )}
                </section>

                {hasRestrictions && (
                  <>
                    <hr className={styles.sidebarDivider} />
                    <section>
                      <h2 className={styles.sidebarSectionTitle}>Restrictions</h2>
                      {restrictionRows.map((row) => (
                        <div key={`${row.label}-${row.value}`} className={styles.restrictionRow}>
                          <span className={styles.restrictionLabel}>{row.label}</span>
                          <span className={styles.pillNeutral}>{row.value}</span>
                        </div>
                      ))}
                    </section>
                  </>
                )}

                {immunizationLabels.length > 0 && (
                  <>
                    <hr className={styles.sidebarDivider} />
                    <section>
                      <h2 className={styles.sidebarSectionTitle}>Immunizations</h2>
                      <div className={styles.immunizationPills}>
                        {immunizationLabels.map((label, idx) => (
                          <span key={`${label}-${idx}`} className={styles.immunizationBadge}>
                            {label}
                          </span>
                        ))}
                      </div>
                      {/* <p className={styles.immunizationCdcNote}>
                        Check with the CDC website for the immunizations required for this event.
                      </p> */}
                    </section>
                  </>
                )}
              </aside>

              <div className={styles.eventRegistrationArea}>{returnRegistrations()}</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default EventDetails;